import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { BulbIcon } from "../../assets/icons";
import { Accordion, CommunicationMap, StatsChart } from ".";
import { generateStarPickerStats } from "../../util/workshop.util";

const TeamCommunicationPreferences = ({
  starSelections = {},
  attitudesSelections: { wordScores, modSelections } = {},
  isPdf = false,
  platform,
  mftPublic,
  mftPublicTeam,
  showStats = true
}) => {
  const { t, i18n } = useTranslation();

  const [communicationStats, setCommunicationStats] = useState([]);
  const [attitudesStats, setAttitudesStats] = useState([]);

  const attitudesList = t("resources.attitudes", {
    returnObjects: true
  });
  const starsList = t("resources.starsList", { returnObjects: true });

  const translation =
    platform === "mft" ? "mft.communicationPreferences" : "communicationPreferences";

  const teamCommunicationPreferences = t(translation, {
    returnObjects: true
  });

  const selectedAttitudes = Object.keys(modSelections || {})
    .filter((key) => modSelections[key])
    .map((key) => attitudesList[parseInt(key)])
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b, i18n.language));

  const content = () => {
    const { description, tips = [] } = teamCommunicationPreferences?.content || {};

    const mftPublicTranslations = t("mft.communicationPreferences.mftPublic", {
      returnObjects: true
    });
    const mftPublicTeamTranslations = t("mft.communicationPreferences.mftPublicTeam", {
      returnObjects: true
    });
    const finalTips = mftPublicTeam
      ? mftPublicTeamTranslations.tips
      : mftPublic
        ? mftPublicTranslations.tips
        : tips;

    return (
      <div className="block w-full">
        {!isPdf && (
          <p
            className="mb-4"
            dangerouslySetInnerHTML={{
              __html: mftPublicTeam
                ? mftPublicTeamTranslations.description
                : mftPublic
                  ? mftPublicTranslations.description
                  : description
            }}
          />
        )}

        {isPdf && (
          <div
            className={`flex items-center justify-between cursor-pointer select-none ${isPdf ? "mb-2" : "mb-6"}`}>
            <div className="flex items-center gap-2">
              <span
                data-testid="accordion-icon"
                className={`block ${isPdf ? "w-10 h-10" : "w-16 h-16"}`}
                dangerouslySetInnerHTML={{ __html: teamCommunicationPreferences?.icon }}
              />
              <div
                className="text-gray-250 text-xl font-extrabold"
                dangerouslySetInnerHTML={{ __html: teamCommunicationPreferences?.title }}
              />
            </div>
            <div className="flex items-center gap-2">
              {teamCommunicationPreferences?.pdf?.mapKeys?.map((wordCloudKey) => (
                <span
                  key={wordCloudKey}
                  className="bg-orange-200 px-6 py-1 rounded-sm min-w-24 text-center"
                  dangerouslySetInnerHTML={{ __html: wordCloudKey }}
                />
              ))}
            </div>
          </div>
        )}

        {finalTips && finalTips?.length > 0 && (
          <div
            className={`rounded-lg border-2 border-gray-250/40 bg-orange-700 grid grid-cols-[auto_1fr] items-center gap-3 ${
              isPdf ? "px-4 py-2" : "p-4"
            }`}>
            <span className="hidden sm:block w-12 h-12 shrink-0">
              <BulbIcon />
            </span>
            <ul className={`list-disc pl-6 ${isPdf ? "text-xs" : ""}`}>
              {finalTips.map((tip, index) => (
                <li
                  key={index}
                  className="text-[#575756]"
                  dangerouslySetInnerHTML={{ __html: tip }}
                />
              ))}
            </ul>
          </div>
        )}

        <div className={`w-full ${isPdf ? "bg-orange-200 p-2 rounded-2xl mt-2" : "mt-4"}`}>
          <CommunicationMap starSelections={starSelections} isPdf={isPdf} platform={platform} />
        </div>

        {!isPdf && showStats && (
          <div className="rounded-lg border-2 border-gray-250/40 bg-[#FFF] p-6 mt-6">
            <StatsChart data={communicationStats} halfCol={true} />
          </div>
        )}

        {selectedAttitudes?.length > 0 && (showStats || isPdf) && (
          <div
            className={
              isPdf
                ? "flex items-center justify-between gap-4 rounded-xl bg-orange-700 px-4 py-3 mt-4"
                : "flex flex-col items-center mt-6"
            }>
            <p
              className={isPdf ? "text-gray-250 font-bold uppercase text-sm shrink-0" : "mb-4"}
              dangerouslySetInnerHTML={{ __html: teamCommunicationPreferences?.attitudes?.heading }}
            />
            <div className={isPdf ? "flex flex-wrap gap-2 justify-end" : "flex flex-wrap gap-2"}>
              {selectedAttitudes.map((attitude) => (
                <span
                  key={attitude}
                  className={
                    isPdf
                      ? "bg-orange-100 px-4 py-1.5 rounded-lg text-white-100 text-sm font-medium uppercase whitespace-nowrap"
                      : "bg-orange-100 px-6 py-1 rounded-lg text-white-100 min-w-36 text-center"
                  }>
                  {attitude}
                </span>
              ))}
            </div>
          </div>
        )}

        {!isPdf && attitudesStats && attitudesStats.length > 0 && showStats && (
          <div className="rounded-lg border-2 border-gray-250/40 bg-[#FFF] p-6 mt-6">
            <StatsChart data={attitudesStats} />
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (isPdf || !wordScores) return;

    const temp = [];

    try {
      Object.keys(wordScores).forEach((wordKey, index) => {
        const score = wordScores[wordKey];
        temp.push({
          text: attitudesList[wordKey],
          value: score,
          color: modSelections[wordKey] ? "#E59536" : "#FFEDD1",
          textColor: modSelections[wordKey] ? "#FFF" : "#E59536"
        });
      });

      setAttitudesStats(temp);
    } catch (error) {
      console.error("Error in TeamCommunicationPreferences", error);
    }
  }, [isPdf, wordScores, modSelections]);

  useEffect(() => {
    if (isPdf || !starsList || !starSelections) return;

    try {
      const temp = generateStarPickerStats(starSelections, starsList);

      setCommunicationStats(temp);
    } catch (error) {
      console.error("Error in TeamCommunicationPreferences", error);
    }
  }, [isPdf, starSelections]);

  return (
    <div
      id="communicationPreferences"
      className={
        isPdf ? "h-screen px-4 flex flex-col justify-start items-center overflow-hidden" : "mb-12"
      }>
      <Accordion
        title={isPdf ? null : teamCommunicationPreferences?.title}
        icon={isPdf ? null : teamCommunicationPreferences?.icon}
        content={content()}
        isPdf={isPdf}
      />
    </div>
  );
};

export default TeamCommunicationPreferences;
